import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './Offers.css';

interface IntegrationOffer {
  id: number;
  name: string;
  external_campaign_id: string;
  last_synced_at: string | null;
  is_active: boolean;
  payout: number | string;
}

interface IntegrationStatus {
  success: boolean;
  totalOffers: number;
  lastSync: string | null;
  offers: IntegrationOffer[];
}

interface AdvertiserOption {
  id: string;
  name: string;
  displayName: string;
}

const AVAILABLE_ADVERTISERS: AdvertiserOption[] = [
  { id: 'dlab', name: 'dlab', displayName: 'D-LAB' },
  // Add more advertisers here in the future
  // { id: 'xyz', name: 'xyz', displayName: 'XYZ Network' },
];

const AdvertiserIntegrations: React.FC = () => {
  const navigate = useNavigate();
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<string>('dlab');
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, [selectedAdvertiser]);

  useEffect(() => {
    if (autoSyncEnabled && selectedAdvertiser === 'dlab') {
      const interval = setInterval(() => {
        fetchStatus(true);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [autoSyncEnabled, selectedAdvertiser]);

  const fetchStatus = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      if (selectedAdvertiser === 'dlab') {
        const response = await apiService.getDLabStatus();
        setStatus(response);
      }
    } catch (error: any) {
      console.error(`Error fetching ${selectedAdvertiser} status:`, error);
      if (!silent) {
        setMessage({ type: 'error', text: error.message || `Failed to fetch ${selectedAdvertiser} status` });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSync = async (silent = false) => {
    try {
      if (!silent) {
        setSyncing(true);
        setMessage(null);
      }

      let response;
      if (selectedAdvertiser === 'dlab') {
        response = await apiService.syncDLabCampaigns();
      }

      if (!silent && response) {
        setMessage({
          type: 'success',
          text: `Sync completed! Created: ${response.created}, Updated: ${response.updated}, Errors: ${response.errors}`
        });
      }

      await fetchStatus(true);
    } catch (error: any) {
      console.error(`Error syncing ${selectedAdvertiser}:`, error);
      if (!silent) {
        setMessage({ type: 'error', text: error.message || `Failed to sync ${selectedAdvertiser} campaigns` });
      }
    } finally {
      if (!silent) {
        setSyncing(false);
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAdvertiserDisplayName = () => {
    return AVAILABLE_ADVERTISERS.find((adv) => adv.id === selectedAdvertiser)?.displayName || selectedAdvertiser.toUpperCase();
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Advertiser Integrations</h1>
        </div>
        <div className="page-empty-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="offers-page">
      <div className="page-header">
        <h1>Advertiser Integrations</h1>
        <div className="integration-controls">
          <label className="integration-toggle">
            <input
              type="checkbox"
              checked={autoSyncEnabled}
              onChange={(e) => setAutoSyncEnabled(e.target.checked)}
            />
            Auto-refresh (30s)
          </label>
          <button className="btn btn-primary" onClick={() => handleSync(false)} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>

      <div className="offers-section">
        <div className="integration-selector">
          <label className="integration-selector-label">Select Advertiser Integration:</label>
          <select
            value={selectedAdvertiser}
            onChange={(e) => setSelectedAdvertiser(e.target.value)}
            className="integration-selector-select"
          >
            {AVAILABLE_ADVERTISERS.map((adv) => (
              <option key={adv.id} value={adv.id}>
                {adv.displayName}
              </option>
            ))}
          </select>
          {AVAILABLE_ADVERTISERS.length === 1 && (
            <small className="integration-selector-help">
              More integrations will appear here as they are added
            </small>
          )}
        </div>

        {message && <div className={`status-banner ${message.type}`}>{message.text}</div>}

        <div className="integration-stats">
          <div className="integration-stat">
            <div className="integration-stat-label">Total {getAdvertiserDisplayName()} Offers</div>
            <div className="integration-stat-value">{status?.totalOffers || 0}</div>
          </div>
          <div className="integration-stat">
            <div className="integration-stat-label">Last Sync</div>
            <div className="integration-stat-value">{formatDate(status?.lastSync || null)}</div>
          </div>
          <div className="integration-stat">
            <div className="integration-stat-label">Integration Status</div>
            <div className={`integration-stat-value ${autoSyncEnabled ? 'integration-stat-success' : ''}`}>
              {autoSyncEnabled ? 'Auto-Sync Active' : 'Manual Sync'}
            </div>
          </div>
        </div>

        <h2 className="section-title">Synced Campaigns</h2>

        {status && status.offers.length === 0 ? (
          <div className="page-empty-state">
            <p>No campaigns synced yet. Click "Sync Now" to import campaigns from {getAdvertiserDisplayName()}.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="offers-table">
              <thead>
                <tr>
                  <th>Offer ID</th>
                  <th>Campaign Name</th>
                  <th>External ID</th>
                  <th>Payout</th>
                  <th>Status</th>
                  <th>Last Synced</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {status?.offers.map((offer) => (
                  <tr key={offer.id}>
                    <td>{offer.id}</td>
                    <td>{offer.name}</td>
                    <td>
                      <code className="inline-code">{offer.external_campaign_id}</code>
                    </td>
                    <td>${typeof offer.payout === 'number' ? offer.payout.toFixed(2) : parseFloat(String(offer.payout)).toFixed(2)}</td>
                    <td>
                      <button className={`status-badge ${offer.is_active ? 'active' : 'inactive'}`} disabled>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>{formatDate(offer.last_synced_at)}</td>
                    <td>
                      <div className="action-links">
                        <button
                          type="button"
                          className="action-link"
                          onClick={() => navigate(`/offers/view/${offer.id}`)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedAdvertiser === 'dlab' && (
          <div className="mapping-panel">
            <h3 className="section-title">D-LAB Macro Mapping</h3>
            <table className="mapping-table">
              <thead>
                <tr>
                  <th>Your Parameter</th>
                  <th>D-LAB Macro</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code className="inline-code">click_id</code></td>
                  <td><code className="inline-code">{'{click_id}'}</code></td>
                  <td>Unique click identifier</td>
                </tr>
                <tr>
                  <td><code className="inline-code">aff_sub1</code></td>
                  <td><code className="inline-code">{'{aff_sub}'}</code></td>
                  <td>Publisher sub ID 1</td>
                </tr>
                <tr>
                  <td><code className="inline-code">aff_sub2</code></td>
                  <td><code className="inline-code">{'{aff_sub2}'}</code></td>
                  <td>Publisher sub ID 2</td>
                </tr>
                <tr>
                  <td><code className="inline-code">gaid</code></td>
                  <td><code className="inline-code">{'{gaid}'}</code></td>
                  <td>Google Advertising ID</td>
                </tr>
                <tr>
                  <td><code className="inline-code">idfa</code></td>
                  <td><code className="inline-code">{'{idfa}'}</code></td>
                  <td>iOS Advertising ID</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvertiserIntegrations;
